/*
 * GUSTA CUSTOMIZATION: PRODUCT + QUICK VIEW PICKUP DETAILS
 *
 * Responsibilities:
 * - Adds a business-day clarification to Shopify's native 24-hour pickup message.
 * - Enhances Warehouse's native pickup modal after Warehouse has initialized it.
 * - Supports both full product pages and dynamically rendered quick views.
 * - Adds merchant-editable pickup hours and intersection details while keeping
 *   Shopify's native location address, phone, pickup status, and modal behavior.
 *
 * SAFE THEME UPDATE STANDARD
 * - Gusta-owned isolated asset
 * - stable Warehouse semantic selectors only
 * - no generated Shopify section/block IDs
 * - no hard-coded product/location IDs
 * - no vendor/app code changes
 * - native pickup markup remains the functional source of truth
 * - idempotent markers allow safe re-application after variant rerenders
 */
(function () {
  'use strict';

  var PAGE_SELECTOR = 'body.warehouse--v4';
  var INLINE_STOCK_SELECTOR = PAGE_SELECTOR + ' .store-availability-information__stock';
  var MODAL_SELECTOR = PAGE_SELECTOR + ' .store-availabilities-modal';
  var OPEN_TRIGGER_SELECTOR = PAGE_SELECTOR + ' .store-availability-information__link[data-action="open-modal"][aria-controls]';
  var CONFIG_SELECTOR = 'script[data-gusta-pickup-config]';
  var TARGET_TEXT = 'usually ready in 24 hours';
  var NOTE_SELECTOR = '[data-gusta-pickup-business-days]';
  var scheduledInline = false;

  function normalize(value) {
    return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function readConfig() {
    var script = document.querySelector(CONFIG_SELECTOR);

    if (!script) {
      return {};
    }

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  function appendBusinessDayNote(node) {
    if (!node || node.querySelector(NOTE_SELECTOR)) {
      return;
    }

    if (normalize(node.textContent).indexOf(TARGET_TEXT) === -1) {
      return;
    }

    var note = document.createElement('span');
    note.setAttribute('data-gusta-pickup-business-days', '');
    note.textContent = ' (business days only)';
    node.appendChild(note);
  }

  function decorateInlinePickupTime() {
    document.querySelectorAll(INLINE_STOCK_SELECTOR).forEach(appendBusinessDayNote);
  }

  function decorateModalPickupTime(modal) {
    if (!modal) {
      return;
    }

    modal.querySelectorAll('.store-availability-list__stock').forEach(appendBusinessDayNote);
  }

  function createHeading(text, marker) {
    var heading = document.createElement('p');
    heading.className = 'gusta-pickup-modal__heading';
    heading.setAttribute(marker, '');
    heading.textContent = text;
    return heading;
  }

  function addStoreHeading(contact) {
    if (!contact || contact.querySelector('[data-gusta-pickup-store-heading]')) {
      return;
    }

    contact.insertBefore(createHeading('Store information', 'data-gusta-pickup-store-heading'), contact.firstChild);
  }

  function findAddressParagraph(contact) {
    if (!contact) {
      return null;
    }

    return contact.querySelector('p:not([data-gusta-pickup-store-heading])');
  }

  function normalizeProvinceDisplay(address) {
    if (!address) {
      return;
    }

    Array.prototype.forEach.call(address.childNodes, function (node) {
      if (node.nodeType !== 3 || !/\bON\b/.test(node.nodeValue || '')) {
        return;
      }

      node.nodeValue = node.nodeValue.replace(/\bON\b/g, 'Ontario');
    });
  }

  function insertIntersection(address, intersection) {
    if (!address || !intersection || address.querySelector('[data-gusta-pickup-intersection]')) {
      return;
    }

    var nodes = Array.prototype.slice.call(address.childNodes);
    var unitNode = null;

    nodes.some(function (node) {
      if (node.nodeType === 3 && /^\s*unit\b/i.test(node.nodeValue || '')) {
        unitNode = node;
        return true;
      }

      return false;
    });

    var anchorBreak = null;

    if (unitNode) {
      var sibling = unitNode.nextSibling;
      while (sibling) {
        if (sibling.nodeType === 1 && sibling.tagName === 'BR') {
          anchorBreak = sibling;
          break;
        }
        sibling = sibling.nextSibling;
      }
    }

    if (!anchorBreak) {
      anchorBreak = address.querySelector('br');
    }

    if (!anchorBreak || !anchorBreak.parentNode) {
      return;
    }

    var intersectionLine = document.createElement('span');
    intersectionLine.className = 'gusta-pickup-modal__intersection';
    intersectionLine.setAttribute('data-gusta-pickup-intersection', '');
    intersectionLine.textContent = intersection;

    var lineBreak = document.createElement('br');
    lineBreak.setAttribute('data-gusta-pickup-intersection-break', '');

    anchorBreak.parentNode.insertBefore(intersectionLine, anchorBreak.nextSibling);
    anchorBreak.parentNode.insertBefore(lineBreak, intersectionLine.nextSibling);
  }

  function formatPhone(raw) {
    var digits = String(raw || '').replace(/\D/g, '');

    if (digits.length === 10) {
      digits = '1' + digits;
    }

    if (digits.length !== 11 || digits.charAt(0) !== '1') {
      return null;
    }

    return {
      display: '+1 ' + digits.slice(1, 4) + '-' + digits.slice(4, 7) + '-' + digits.slice(7),
      href: 'tel:+' + digits
    };
  }

  function enhancePhone(contact) {
    if (!contact || contact.querySelector('[data-gusta-pickup-phone]')) {
      return;
    }

    var walker = document.createTreeWalker(contact, NodeFilter.SHOW_TEXT);
    var candidates = [];
    var current;

    while ((current = walker.nextNode())) {
      candidates.push(current);
    }

    candidates.some(function (node) {
      if (!node.parentNode || (node.parentElement && node.parentElement.closest('a'))) {
        return false;
      }

      var phone = formatPhone(node.nodeValue);

      if (!phone) {
        return false;
      }

      var link = document.createElement('a');
      link.className = 'gusta-pickup-modal__phone';
      link.setAttribute('data-gusta-pickup-phone', '');
      link.href = phone.href;
      link.textContent = phone.display;
      link.setAttribute('aria-label', 'Call Gusta Supplies at ' + phone.display);
      node.parentNode.replaceChild(link, node);
      return true;
    });
  }

  function buildHours(pickupHours) {
    var lines = String(pickupHours || '')
      .split(/\n+/)
      .map(function (line) { return line.trim(); })
      .filter(Boolean);

    if (!lines.length) {
      return null;
    }

    var wrapper = document.createElement('div');
    wrapper.className = 'gusta-pickup-modal__hours';
    wrapper.setAttribute('data-gusta-pickup-hours', '');
    wrapper.appendChild(createHeading('Pickup hours', 'data-gusta-pickup-hours-heading'));

    lines.forEach(function (line, index) {
      var item = document.createElement('span');
      item.className = 'gusta-pickup-modal__hours-line';

      if (index === lines.length - 1 && lines.length > 1) {
        item.className += ' gusta-pickup-modal__hours-line--advisory';
      }

      item.textContent = line;
      wrapper.appendChild(item);
    });

    return wrapper;
  }

  function addHours(info, pickupHours) {
    if (!info || info.querySelector('[data-gusta-pickup-hours]')) {
      return Boolean(info && info.querySelector('[data-gusta-pickup-hours]'));
    }

    var hours = buildHours(pickupHours);

    if (!hours) {
      return false;
    }

    info.appendChild(hours);
    return true;
  }

  function enhanceModal(modal) {
    if (!modal) {
      return;
    }

    decorateModalPickupTime(modal);

    var items = modal.querySelectorAll('.store-availability-list__item');

    // Shop-level Gusta details are only added while Shopify exposes one pickup
    // location. If additional locations are enabled later, native Warehouse
    // per-location information remains untouched rather than sharing one set of hours.
    if (items.length !== 1) {
      return;
    }

    var config = readConfig();
    var item = items[0];
    var info = item.querySelector('.store-availability-list__item-info');
    var contact = item.querySelector('.store-availability-list__contact');

    if (!info || !contact) {
      return;
    }

    addStoreHeading(contact);

    var address = findAddressParagraph(contact);
    normalizeProvinceDisplay(address);
    insertIntersection(address, config.storeIntersection);
    enhancePhone(contact);

    if (addHours(info, config.pickupHours)) {
      item.classList.add('gusta-pickup-modal--enhanced');
    }
  }

  function findControlledModal(trigger) {
    var controlId = trigger && trigger.getAttribute('aria-controls');
    var modal = controlId ? document.getElementById(controlId) : null;

    if (modal && modal.matches('.store-availabilities-modal')) {
      return modal;
    }

    var modals = Array.prototype.slice.call(document.querySelectorAll(MODAL_SELECTOR));
    var visible = modals.filter(function (candidate) {
      return candidate.hasAttribute('open') || candidate.getClientRects().length > 0;
    });

    if (visible.length) {
      return visible[visible.length - 1];
    }

    return modals.length === 1 ? modals[0] : null;
  }

  function enhanceFromTrigger(trigger) {
    function run() {
      enhanceModal(findControlledModal(trigger));
    }

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(run);
    });

    // Some Warehouse modal work completes after the click task. A short,
    // idempotent fallback covers that timing without altering native events.
    window.setTimeout(run, 120);
  }

  function enhanceExistingModals() {
    document.querySelectorAll(MODAL_SELECTOR).forEach(enhanceModal);
  }

  function scheduleInlineDecoration() {
    if (scheduledInline) {
      return;
    }

    scheduledInline = true;
    window.requestAnimationFrame(function () {
      scheduledInline = false;
      decorateInlinePickupTime();
    });
  }

  function initialize() {
    decorateInlinePickupTime();
    enhanceExistingModals();

    document.addEventListener('click', function (event) {
      var target = event.target;
      var trigger = target && target.closest ? target.closest(OPEN_TRIGGER_SELECTOR) : null;

      if (trigger) {
        enhanceFromTrigger(trigger);
      }
    });

    var main = document.getElementById('main');

    if (main && 'MutationObserver' in window) {
      new MutationObserver(scheduleInlineDecoration).observe(main, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
  }

  // This asset is intentionally loaded before Warehouse's deferred theme.js.
  // Waiting for DOMContentLoaded ensures all deferred scripts later in the
  // document, including theme.js and custom.js, finish native modal setup first.
  if (document.readyState === 'complete') {
    initialize();
  } else {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  }

  document.addEventListener('shopify:section:load', function () {
    scheduleInlineDecoration();
    window.requestAnimationFrame(enhanceExistingModals);
  });
})();
